/**
 * @fileoverview Shared creation-method picker modal for dashboard templates (`NewDashboardEntryModal`).
 * @module ui-admin/components/NewDashboardEntryModal
 *
 * Responsibilities: - Present dashboard creation methods (`Start from Scratch`, `From System Library`,
 *                     `Duplicate Existing`) as clickable cards (mirrors `NewDocumentEntryModal`).
 * Dependencies:     - React, lucide-react icons.
 * Architecture:     - Pure presentational modal; zero data access. Shared by `tenant-web`
 *                     (`DashboardViewerPage`, `DashboardTemplates`) and `system-web`
 *                     (`SystemDashboardsPage`).
 * Notes:            - `showSystemLibraryOption` defaults to `true` in `tenant-web` and can be set
 *                     to `false` in `system-web`.
 */

import React from 'react';
import { X, PenLine, BookOpen, Copy } from 'lucide-react';

/**
 * @contract DashboardCreationMethod
 * Purpose: Identifies the dashboard creation path chosen by the user.
 * Fields:  'scratch' | 'library' | 'duplicate'
 * Rules:   'library' is only offered when `showSystemLibraryOption !== false`.
 * Sync:    N/A.
 */
export type DashboardCreationMethod = 'scratch' | 'library' | 'duplicate';

/**
 * @contract NewDashboardEntryModalProps
 * Purpose: Props for `NewDashboardEntryModal`.
 * Fields:  open, onClose, onSelect, showSystemLibraryOption?, libraryLabel?, libraryDescription?
 * Rules:   Clicking any card invokes `onSelect(method)` and `onClose()`.
 * Sync:    N/A.
 */
export interface NewDashboardEntryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (method: DashboardCreationMethod) => void;
  showSystemLibraryOption?: boolean;
  libraryLabel?: string;
  libraryDescription?: string;
}

interface MethodCard {
  method: DashboardCreationMethod;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
  iconBg: string;
  border: string;
  hoverBorder: string;
}

/**
 * @component NewDashboardEntryModal
 * Purpose:     Modal dialog that lets the user pick a dashboard creation method before entering
 *              the 3-pane dashboard builder.
 * Props:       NewDashboardEntryModalProps.
 * Behavior:    Renders method cards; clicking any card calls `onSelect(method)` then `onClose()`.
 * Data Source: none (presentation-only).
 * Constraints: no Dexie/API calls.
 * Performance: stateless.
 */
export function NewDashboardEntryModal({
  open,
  onClose,
  onSelect,
  showSystemLibraryOption = true,
  libraryLabel = 'From System Library',
  libraryDescription = 'Browse and adopt a ready-made dashboard from the System Library and customize it for your department.',
}: NewDashboardEntryModalProps) {
  if (!open) return null;

  const cards: MethodCard[] = [
    {
      method: 'scratch',
      icon: <PenLine className="h-8 w-8" />,
      title: 'Start from Scratch',
      description:
        'Open a blank 12-column canvas in the 3-pane dashboard builder — add KPI cards, ECharts, data tables, and global filters.',
      accent: 'text-orange-600',
      iconBg: 'bg-orange-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-orange-400 hover:shadow-orange-100',
    },
    ...(showSystemLibraryOption
      ? [
          {
            method: 'library' as const,
            icon: <BookOpen className="h-8 w-8" />,
            title: libraryLabel,
            description: libraryDescription,
            accent: 'text-blue-600',
            iconBg: 'bg-blue-50',
            border: 'border-gray-200',
            hoverBorder: 'hover:border-blue-400 hover:shadow-blue-100',
          },
        ]
      : []),
    {
      method: 'duplicate',
      icon: <Copy className="h-8 w-8" />,
      title: 'Duplicate Existing',
      description:
        'Clone an existing dashboard template and customize its widgets, filters, and layout.',
      accent: 'text-purple-600',
      iconBg: 'bg-purple-50',
      border: 'border-gray-200',
      hoverBorder: 'hover:border-purple-400 hover:shadow-purple-100',
    },
  ];

  const handleSelect = (method: DashboardCreationMethod) => {
    onSelect(method);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-dashboard-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-xs"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 id="new-dashboard-modal-title" className="text-xl font-semibold text-gray-900">
            Create New Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">How would you like to start?</p>
        </div>

        <div className="px-8 pb-8 flex flex-col gap-3">
          {cards.map((card) => (
            <button
              key={card.method}
              type="button"
              data-testid={`new-dashboard-option-${card.method}`}
              onClick={() => handleSelect(card.method)}
              className={`
                group flex items-start gap-4 w-full text-left
                rounded-xl border-2 ${card.border} ${card.hoverBorder}
                p-5 transition-all duration-150
                hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-orange-400
              `}
            >
              <span
                className={`mt-0.5 shrink-0 rounded-xl p-2.5 ${card.iconBg} ${card.accent} transition-colors`}
              >
                {card.icon}
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-base font-semibold text-gray-900 transition-colors">
                  {card.title}
                </span>
                <span className="mt-1 text-sm text-gray-500 leading-relaxed">
                  {card.description}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default NewDashboardEntryModal;
